import React from "react";
import {
  CoursesResult,
  CreateCustomFlashpackResponse,
  LeaderboardsResponse,
  CustomFlashpack,
  DeleteAccountResponse,
  FilterInfo,
  FlashpackItem,
  FlashpackSubject,
  FlashpackType,
  Lesson,
  LessonAward,
  LessonOrUnitGroup,
  LessonRecording,
  LessonsInProgress,
  ManageSubscriptionUrlResponse,
  ModifyCustomFlashpackRequest,
  NextLessonResponse,
  PracticeMetadataResult,
  Profile,
  SchoolDashboard,
  SchoolDashboardError,
  SchoolGroupList,
  SchoolInsightReportList,
  SchoolList,
  SchoolRenewalList,
  SpeakingRate,
  StripeSubscriptionProductId,
  StripeCheckoutSession,
  StripeSubscriptionProduct,
  TeacherAvatarInfo,
  TeacherLessonAssignments,
  TeacherSectionStudentList,
  UnitWithLessons,
  User,
  ReconcileStripeCheckoutSessionResult,
  FeedbackRequest,
  SchoolStudent,
  SchoolTeacher,
  CustomSection,
  MilestonesResponse,
  LessonStandardsResponse,
  SearchResponse,
  StandardsAttributionsResponse,
} from "../api";
import { MakeRequestResponse } from "./AccountContext";

export interface ApiContextValue {
  getCourses: (flags: { [key: string]: string }) => Promise<CoursesResult>;
  getPracticeMetadata: (
    profileId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<PracticeMetadataResult>;
  getHomeMetadata: (
    profileId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<{
    groups: LessonOrUnitGroup[];
    filter_info: FilterInfo;
  }>;
  getLeaderboards: () => Promise<LeaderboardsResponse>;
  getAwardsMetadata: (profileId: number | null) => Promise<{
    unlocked_awards: [LessonAward];
  }>;
  getMilestonesMetadata: (
    profileId: number | null
  ) => Promise<MilestonesResponse>;
  getStudentAssignmentsMetadata: (
    profileId: number | null,
    teacherId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<{
    groups: [LessonOrUnitGroup];
  }>;
  getMyClassMetadata: (
    profileId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<{
    lesson_assignments: [Lesson];
  }>;
  getUnitWithLessons: (
    unitId: number,
    profileId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<{ unit: UnitWithLessons }>;
  getLesson: (
    lessonId: number,
    version: number | null,
    customFlashpackId: number | null
  ) => Promise<Lesson>;
  deleteLessonState: (
    lessonId: number,
    version: number,
    profileId: number
  ) => void;
  makeGuestUser: () => Promise<{ access_token: string; user: User }>;
  getTeacherSections: () => Promise<{ sections: TeacherSectionStudentList[] }>;
  deleteCustomSection: (schoolId: number, sectionId: number) => void;
  getCustomSection: (
    schoolId: number,
    sectionId: number
  ) => Promise<CustomSection>;
  updateCustomSection: (
    schoolId: number,
    sectionId: number,
    name: string,
    student_profile_ids: number[],
    teachers: number[]
  ) => Promise<CustomSection>;
  getCustomSections: (schoolId: number) => Promise<CustomSection[]>;
  createCustomSection: (
    schoolId: number,
    name: string,
    student_profile_ids: number[],
    teacher_user_ids: number[]
  ) => Promise<CustomSection>;
  getTeachers: () => Promise<{ teachers: SchoolTeacher[] }>;
  getSchoolDashboard: (
    queryset: string
  ) => Promise<SchoolDashboard | SchoolDashboardError>;
  getSchoolList: () => Promise<SchoolList>;
  getSchoolGroupList: () => Promise<SchoolGroupList>;
  getSchoolRenewalList: () => Promise<SchoolRenewalList>;
  getSchoolInsightReports: () => Promise<SchoolInsightReportList>;
  createLessonAssignments: (
    data: TeacherLessonAssignments
  ) => Promise<MakeRequestResponse>;
  getSingleLessonAssignments: (
    lessonId: number,
    customFlashpackId: number | null
  ) => Promise<{ student_list: SchoolStudent[] }>;
  deleteSingleLessonAssignments: (
    lessonId: number,
    customFlashpackId: number | null,
    profileIdsToUnassign: number[]
  ) => Promise<MakeRequestResponse>;
  modifyProfile: (
    profileId: number,
    firstName: string,
    firstNamePhonetics: string[],
    speakingRate: SpeakingRate,
    asrEnglishAccent: number,
    teacherAvatar: number,
    closedCaptionsEnabled: boolean,
    dontRetryErrors: boolean
  ) => Promise<Profile | undefined>;
  createProfile: (
    firstName: string,
    firstNamePhonetics: string[],
    speakingRate: SpeakingRate,
    asrEnglishAccent: number,
    teacherAvatar: number,
    dontRetryErrors: boolean
  ) => Promise<Profile | undefined>;
  deleteProfile: (profileId: number) => Promise<void>;
  deleteAccount: () => Promise<DeleteAccountResponse>;
  checkUserInput: (
    firstName: string
  ) => Promise<{ result: string; error_message?: string } | undefined>;
  previewPhonics: (ipa: string, firstName?: string) => Promise<ArrayBuffer>;
  getTeacherAvatars: () => Promise<{
    teacher_avatars_info: TeacherAvatarInfo[];
  }>;
  createLessonExecution: (
    lessonId: number,
    lessonVersion: number,
    profileId: number
  ) => Promise<{ lesson_execution_id: string }>;
  maybeUpdateLessonExecution: (
    lessonId: number,
    lessonExecutionId: string,
    deltaLengthSeconds: number,
    didComplete: boolean
  ) => Promise<boolean>;
  obtainNextLesson: (
    lessonId: number,
    profileId: number,
    version: number,
    customFlashpackId: number | null,
    lessonsInProgress: LessonsInProgress
  ) => Promise<NextLessonResponse>;
  getCustomFlashpack: (customFlashpackId: number) => Promise<CustomFlashpack>;
  modifyCustomFlashpack: (
    customFlashpackId: number,
    data: ModifyCustomFlashpackRequest
  ) => Promise<any>;
  createCustomFlashpack: (
    flashpack_type: FlashpackType,
    profile_id: number,
    items: string[]
  ) => Promise<CreateCustomFlashpackResponse>;
  deleteCustomFlashpack: (customFlashpackId: number) => Promise<void>;
  getFlashpackSubjects: () => Promise<{ subjects: FlashpackSubject[] }>;
  getFlashpackItems: (flashpackType: FlashpackType) => Promise<FlashpackItem[]>;
  getLessonRecordings: () => Promise<LessonRecording[]>;
  deleteLessonRecording: (recordingId: number) => Promise<void>;
  modifyUser: (
    receivesPostLessonEmails: boolean,
    recordLessonReplays: boolean,
    dontRetryErrors: boolean
  ) => Promise<User | undefined>;
  getManageSubscriptionUrl: () => Promise<ManageSubscriptionUrlResponse>;
  getAvailableSubscriptions: () => Promise<{
    available_subscriptions: StripeSubscriptionProduct[];
  }>;
  createStripeCheckoutSession: (
    productId: StripeSubscriptionProductId
  ) => Promise<StripeCheckoutSession>;
  reconcileStripeCheckoutSession: (
    checkoutSessionId: string,
    bakery: Record<string, string>,
    eventSourceUrl: string
  ) => Promise<ReconcileStripeCheckoutSessionResult>;
  resetPassword: (email: String) => Promise<void>;
  sendFeedback: (data: FeedbackRequest) => Promise<void>;
  getBakery: () => Promise<Record<string, string>>;
  getTeacherResources: () => Promise<{
    email_from_teacher_to_parents_path: string;
  }>;
  search: (
    q: string,
    flags: { [key: string]: string }
  ) => Promise<SearchResponse | undefined>;
  submitSearchFeedback: (searchId: number, feedback: string) => Promise<boolean>;
  getLessonStandards: (lessonId: number) => Promise<LessonStandardsResponse>;
  submitLessonStandardsFeedback: (
    lessonId: number,
    feedback: string
  ) => Promise<boolean>;
  getStandardsAttributions: () => Promise<StandardsAttributionsResponse>;
}

export const ApiContext = React.createContext<ApiContextValue>(null!);
